import { FC } from 'react';
import { Tooltip } from 'react-tooltip';
import { Dropdown } from 'react-bootstrap';
import { useTranslate } from 'hooks/useTranslate';
import { format } from 'date-fns';

import { Document } from 'types';

import { getDocumentIcon } from 'utils/documentIcons';

import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import { useDispatch } from 'react-redux';
import { setModal } from 'modules/_shared/redux/modalActions';
import DocumentViewer from 'modules/_shared/components/Modals/DocumentViewer';
import DocumentsColumn from '../DocumentsColumn';
import { getToggleOptions } from './utils/getToggleOptions';

type Props = {
  index: number;
  document: Document;
  hasOptions?: boolean;
  handleViewDocument?: (index: number) => void; // eslint-disable-line no-unused-vars
  handleDownloadDocument: (index: number) => void; // eslint-disable-line no-unused-vars
  handleRemoveDocument?: (index: number) => void; // eslint-disable-line no-unused-vars
  hasDownload?: boolean;
  isAdminView?: boolean;
  isSummaryVersion?: boolean;
  textSize?: number;
};

const DocumentRow: FC<Props> = ({
  index,
  document,
  hasOptions = true,
  handleViewDocument,
  hasDownload = true,
  isAdminView = true,
  isSummaryVersion,
  textSize = 250,
  handleDownloadDocument,
  handleRemoveDocument,
}: Props) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const isReadOnly = false;

  const documentName = document.name;
  const documentIcon = getDocumentIcon(document);
  const documentDate = document.date;

  const availableOptions = {
    VIEW: (
      <Dropdown.Item
        key={`document-view-option-${index}`}
        onClick={() => handleViewDocument && handleViewDocument(index)}
        disabled={!handleViewDocument}
      >
        <em className="icon ni ni-eye fs-20px" />
        <span>{t('See')}</span>
      </Dropdown.Item>
    ),
    DOWNLOAD: (
      <Dropdown.Item
        key={`document-download-option-${index}`}
        onClick={() => handleDownloadDocument(index)}
      >
        <em className="icon ni ni-download fs-20px" />
        <span>{t('Download')}</span>
      </Dropdown.Item>
    ),
    DELETE: (
      <Dropdown.Item
        key={`document-delete-option-${index}`}
        onClick={() => handleRemoveDocument && handleRemoveDocument(index)}
      >
        <em className="icon ni ni-trash fs-20px" />
        <span>{t('Delete')}</span>
      </Dropdown.Item>
    ),
  };

  const toggleOptions = isAdminView
    ? [
        availableOptions.VIEW,
        availableOptions.DOWNLOAD,
        availableOptions.DELETE,
      ]
    : [availableOptions.VIEW, availableOptions.DOWNLOAD];

  const handleOpenDocument = () => {
    if (isSummaryVersion) {
      dispatch(setModal(<DocumentViewer document={document} size="xl" />));
    }
  };

  return (
    <div className="nk-files-list">
      <div
        className={`nk-file-item nk-file ${
          isSummaryVersion ? 'file-row-hoverable' : ''
        }`}
      >
        <div className="nk-file-info" onClick={handleOpenDocument}>
          <div className="nk-file-title">
            <div className="nk-file-icon">
              <span className="nk-file-icon-type">
                <img src={documentIcon} alt="icon-file" />
              </span>
            </div>
            <div className="nk-file-name">
              <div
                data-tooltip-id="document-name"
                data-tooltip-content={documentName}
                className={`w-${textSize}px d-block text-ellipsis`}
              >
                <span>{documentName}</span>
                <Tooltip id="document-name" place="top" />
              </div>
            </div>
          </div>
        </div>

        <div className="nk-file-members">
          <div className="tb-lead">
            {documentDate ? format(new Date(documentDate), 'dd/MM/yy') : '-'}
          </div>
        </div>

        {hasDownload && (
          <DocumentsColumn
            index={index}
            documentsCount={1}
            documents={[document]}
            tableView={false}
            hideOnMobile={false}
          />
        )}

        {!isReadOnly && hasOptions && isAdminView && (
          <div className="nk-file-meta pr-4">
            <ToggleMenu toggleOptions={toggleOptions} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentRow;
